// src/shared/components/ui/badge.tsx

import type { HTMLAttributes } from 'react'

import { cn } from '@/shared/utils/cn'

export type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info'
export type BadgeStyle = 'subtle' | 'solid' | 'outline'

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone
  badgeStyle?: BadgeStyle
  dot?: boolean
}

const subtleClassMap: Record<BadgeTone, string> = {
  neutral: 'bg-[var(--panel-subtle)] text-[var(--color-text-soft)]',
  brand: 'bg-[var(--color-brand-bg-subtle)] text-[var(--color-brand)]',
  success: 'bg-[var(--color-success-bg)] text-[var(--color-success-fg)]',
  warning: 'bg-[var(--color-warning-bg)] text-[var(--color-warning-fg)]',
  danger: 'bg-[var(--color-danger-bg)] text-[var(--color-danger-fg)]',
  info: 'bg-[var(--color-info-bg)] text-[var(--color-info-fg)]',
}

const solidClassMap: Record<BadgeTone, string> = {
  neutral: 'bg-[var(--color-text-soft)] text-[var(--panel)]',
  brand: 'bg-[var(--color-brand)] text-[var(--color-brand-fg)]',
  success: 'bg-[var(--color-success-solid)] text-white',
  warning: 'bg-[var(--color-warning-solid)] text-white',
  danger: 'bg-[var(--color-danger-solid)] text-white',
  info: 'bg-[var(--color-info-solid)] text-white',
}

const outlineClassMap: Record<BadgeTone, string> = {
  neutral: 'border border-[var(--panel-border)] text-[var(--color-text-soft)]',
  brand: 'border border-[var(--color-brand-border-subtle)] text-[var(--color-brand)]',
  success: 'border border-[var(--color-success-border)] text-[var(--color-success-fg)]',
  warning: 'border border-[var(--color-warning-border)] text-[var(--color-warning-fg)]',
  danger: 'border border-[var(--color-danger-border)] text-[var(--color-danger-fg)]',
  info: 'border border-[var(--color-info-border)] text-[var(--color-info-fg)]',
}

const dotClassMap: Record<BadgeTone, string> = {
  neutral: 'bg-[var(--color-text-soft)]',
  brand: 'bg-[var(--color-brand)]',
  success: 'bg-[var(--color-success-solid)]',
  warning: 'bg-[var(--color-warning-solid)]',
  danger: 'bg-[var(--color-danger-solid)]',
  info: 'bg-[var(--color-info-solid)]',
}

export function Badge({
  className,
  tone = 'neutral',
  badgeStyle = 'subtle',
  dot,
  children,
  ...props
}: BadgeProps) {
  const variantClass =
    badgeStyle === 'solid'
      ? solidClassMap[tone]
      : badgeStyle === 'outline'
        ? outlineClassMap[tone]
        : subtleClassMap[tone]

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs font-medium',
        variantClass,
        className,
      )}
      {...props}
    >
      {dot ? <span className={cn('h-1.5 w-1.5 rounded-full', dotClassMap[tone])} /> : null}
      {children}
    </span>
  )
}
